/**
 * Luật tick màn hình của một gói (ADR-0053), để người tick thấy hệ quả NGAY lúc bấm.
 *
 * Máy chủ áp lại đúng những luật này khi lưu và là bên quyết định; bản ở đây chỉ để màn hình
 * không vẽ ra một tập mà lần lưu sẽ lặng lẽ nới rộng. Luật đọc từ danh mục máy chủ gửi về
 * (`locked`, `requires`, `advisories`) — không tên màn hình nào được viết cứng ở đây.
 */
import type {
  ScreenAdvisory,
  ScreenCatalog,
  ScreenItem,
} from "@/shared/api/platformBilling";

export function allScreens(catalog: ScreenCatalog): ScreenItem[] {
  return catalog.groups.flatMap((g) => g.screens);
}

/** Tick một màn hình, kéo theo mọi màn hình nó cần. */
export function tick(
  catalog: ScreenCatalog,
  ticked: ReadonlySet<string>,
  code: string,
): Set<string> {
  const byCode = new Map(allScreens(catalog).map((s) => [s.code, s]));
  const next = new Set(ticked);
  const pending = [code];
  while (pending.length > 0) {
    const current = pending.pop()!;
    if (next.has(current)) continue;
    next.add(current);
    pending.push(...(byCode.get(current)?.requires ?? []));
  }
  return next;
}

/** Bỏ tick một màn hình, kéo theo mọi màn hình đang cần nó. Màn hình khoá thì giữ nguyên. */
export function untick(
  catalog: ScreenCatalog,
  ticked: ReadonlySet<string>,
  code: string,
): Set<string> {
  const screens = allScreens(catalog);
  if (screens.find((s) => s.code === code)?.locked) return new Set(ticked);
  const next = new Set(ticked);
  const pending = [code];
  while (pending.length > 0) {
    const current = pending.pop()!;
    if (!next.delete(current)) continue;
    pending.push(...screens.filter((s) => s.requires.includes(current)).map((s) => s.code));
  }
  return next;
}

/** Những cảnh báo đang đúng với tập tick hiện tại. */
export function activeAdvisories(
  catalog: ScreenCatalog,
  ticked: ReadonlySet<string>,
): ScreenAdvisory[] {
  return catalog.advisories.filter(
    (a) => ticked.has(a.screen) && a.together.some((code) => ticked.has(code)),
  );
}
